import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface DistributionType {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface DistributionTypeSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const DistributionTypeSelect: React.FC<DistributionTypeSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  
  const distributionTypes: DistributionType[] = [
    {
      id: 'digital',
      name: t('brands.edit.marketingStrategy.distributionType.digital', 'Digital'),
      emoji: '💻',
      description: t('brands.edit.marketingStrategy.distributionType.digitalDescription', 'Primarily online services, digital products, remote delivery')
    },
    {
      id: 'physical',
      name: t('brands.edit.marketingStrategy.distributionType.physical', 'Physical'),
      emoji: '🏪',
      description: t('brands.edit.marketingStrategy.distributionType.physicalDescription', 'Primarily physical locations, in-person services, physical products')
    },
    {
      id: 'hybrid',
      name: t('brands.edit.marketingStrategy.distributionType.hybrid', 'Hybrid'),
      emoji: '🔄',
      description: t('brands.edit.marketingStrategy.distributionType.hybridDescription', 'Combination of physical and digital touchpoints')
    }
  ];
  
  const selectedType = distributionTypes.find(type => type.id === value) || distributionTypes[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedType.emoji}</span>
          <span className="text-gray-700">{selectedType.name}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {distributionTypes.map(type => (
                <button
                  type="button"
                  key={type.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(type.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    type.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{type.emoji}</span>
                    <div>
                      <div className="font-medium">{type.name}</div>
                      <div className="text-xs text-gray-500">{type.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 